import { useState } from "react";
import { X, FloppyDisk } from "@phosphor-icons/react";

export default function SubscriptionForm({ initial, paymentMethods, onClose, onSubmit }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    amount: initial?.amount ?? "",
    payment_method: initial?.payment_method || "transfer",
    invoice_day: initial?.invoice_day ?? 1,
  }));
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt < 0) return;
    const day = parseInt(form.invoice_day, 10);
    if (isNaN(day) || day < 1 || day > 31) return;

    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        amount: amt,
        payment_method: form.payment_method,
        invoice_day: day,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="subs-form-overlay"
    >
      <div
        className="bg-white border border-[#18181b] w-full max-w-lg hard-shadow"
        onClick={(e) => e.stopPropagation()}
        data-testid="subs-form"
      >
        <div className="border-b border-[#d4d4d8] px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="mono-label mb-1">{initial ? "РЕДАКТИРОВАНИЕ" : "НОВЫЙ САЙТ"}</div>
            <h2 className="text-lg font-semibold tracking-tight">
              {initial ? "Редактировать сайт" : "Добавить сайт"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="mono-label block mb-1.5" htmlFor="sub-name">Название сайта</label>
            <input
              id="sub-name"
              data-testid="subs-form-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              placeholder="напр. Аптека.ру"
              className="w-full text-sm border border-[#d4d4d8] px-3 py-2 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
            />
          </div>

          {/* Amount + invoice day */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mono-label block mb-1.5" htmlFor="sub-amount">Сумма, ₽</label>
              <input
                id="sub-amount"
                data-testid="subs-form-amount"
                type="number"
                min="0"
                step="1"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                required
                placeholder="5000"
                className="w-full font-mono text-sm border border-[#d4d4d8] px-3 py-2 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
              />
            </div>
            <div>
              <label className="mono-label block mb-1.5" htmlFor="sub-day">Дата счёта (день)</label>
              <input
                id="sub-day"
                data-testid="subs-form-day"
                type="number"
                min="1"
                max="31"
                value={form.invoice_day}
                onChange={(e) => set("invoice_day", e.target.value)}
                required
                className="w-full font-mono text-sm border border-[#d4d4d8] px-3 py-2 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
              />
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label className="mono-label block mb-1.5">Способ оплаты</label>
            <div className="grid grid-cols-2 gap-2" data-testid="subs-form-method-group">
              {Object.entries(paymentMethods).map(([key, label]) => (
                <label
                  key={key}
                  data-testid={`subs-form-method-${key}`}
                  className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm transition-colors duration-150 ${
                    form.payment_method === key
                      ? "bg-[#0047FF] text-white border-[#0047FF]"
                      : "border-[#d4d4d8] bg-white hover:border-[#18181b]"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value={key}
                    checked={form.payment_method === key}
                    onChange={(e) => set("payment_method", e.target.value)}
                    className="sr-only"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-[#18181b] bg-transparent text-[#18181b] px-4 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="subs-form-submit"
              className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150 disabled:opacity-50"
            >
              <FloppyDisk size={14} weight="bold" />
              {submitting ? "Сохранение..." : initial ? "Сохранить" : "Добавить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
